import React, { useEffect, useRef, useState, useCallback } from "react";
import { Sonometer } from "sonometer/types/sonometer.model";
import { assetBaseUrl } from "./TextPopup";
import { SonometerService } from "services/sonometer.service";

interface ScoreRevelationProps {
  maxRecord?: number;
  currentValue?: number;
  animationInterval?: number;
  sonometer?: Sonometer;
}

const ScoreRevelation: React.FC<ScoreRevelationProps> = ({
  currentValue = 101.3,
  animationInterval = 1100,
  sonometer,
}) => {
  const [displayValue, setDisplayValue] = useState(currentValue);
  const gaugeFillRef = useRef<HTMLDivElement>(null);
  const gaugeMarkerRef = useRef<HTMLDivElement>(null);
  const sonometerService = useRef(new SonometerService()).current; // Use useRef for stable service instance

  // Memoize updateSonometer function. It depends on sonometerService which is now stable.
  const updateSonometer = useCallback(
    async (updatedSonometer: Sonometer) => {
      try {
        const response = await sonometerService.updateSonometerHighScore({
          sonometerToUpdate: updatedSonometer,
        });
        console.log("Sonometer updated:", response);
      } catch (error) {
        console.error("Error updating sonometer:", error);
      }
    },
    [sonometerService]
  );

  const [maxRecord, setMaxRecord] = React.useState<number>(0);

  useEffect(() => {
    let currentMaxRecord = Number(
      localStorage.getItem(`championshipMaxScore-${sonometer?.championshipId}`)
    );
    if (currentMaxRecord && currentMaxRecord < 82.35) {
      currentMaxRecord = 82.35;
    }
    setMaxRecord(currentMaxRecord);
  }, [setMaxRecord, sonometer?.championshipId]);

  console.log("Current", currentValue);

  // Use useRef to capture the initial sonometer prop value to avoid it as a dependency
  const initialSonometerRef = useRef(sonometer);

  useEffect(() => {
    const currentSonometer = initialSonometerRef.current; // Access initial sonometer value

    if (currentSonometer && currentValue !== undefined) {
      const key = `sonometer-data-${currentSonometer.id}`;
      const dataStr = localStorage.getItem(key);

      let maxScore = currentValue;
      let lastScore = currentValue;

      if (dataStr) {
        try {
          const data = JSON.parse(dataStr);

          if (data.history && data.history.length > 0) {
            type Session = {
              score: number;
              timestamp: number;
              sessionId: string;
            };

            maxScore = Math.max(
              ...data.history.map((session: Session) => session.score)
            );

            const lastSession = data.history.reduce(
              (latest: Session, session: Session) =>
                session.timestamp > latest.timestamp ? session : latest
            );
            lastScore = lastSession.score;

            console.log(
              "History sessions:",
              data.history.length,
              "Last Session Score:",
              lastScore,
              "Max Score:",
              maxScore
            );
          }
        } catch (error) {
          console.error("Error parsing sonometer data:", error);
        }
      }

      setDisplayValue(lastScore);

      updateSonometer({
        ...currentSonometer, // Use the initially captured sonometer
        score: maxScore,
        screen: "scoreRevelation",
      });
    }
  }, [currentValue, updateSonometer]); // 'sonometer' is removed from dependencies

  useEffect(() => {
    // Calculate marker position using the exact same formula
    const leftValue = Math.round((maxRecord - 90) * 30.6 + 630);

    // Apply the exact same conditions from the original JavaScript
    if (
      maxRecord < 91.5 ||
      (maxRecord >= 93 && maxRecord <= 95) ||
      (maxRecord >= 96.8 && maxRecord <= 99.4) ||
      (maxRecord >= 101 && maxRecord <= 103)
    ) {
      if (gaugeMarkerRef.current) {
        const img = gaugeMarkerRef.current.querySelector("img");
        if (img) {
          img.style.transform = "rotate(180deg)";
        }
        gaugeMarkerRef.current.style.top = "71px";
      }
    } else {
      // Reset to default if conditions don't match
      if (gaugeMarkerRef.current) {
        const img = gaugeMarkerRef.current.querySelector("img");
        if (img) {
          img.style.transform = "rotate(0deg)";
        }
        gaugeMarkerRef.current.style.top = "calc(50% - 67px)";
      }
    }

    // Set marker position
    if (gaugeMarkerRef.current) {
      gaugeMarkerRef.current.style.left = leftValue + "px";
    }
  }, [maxRecord]);

  useEffect(() => {
    // Exact animation function from original
    const animateGauge = () => {
      if (gaugeFillRef.current) {
        gaugeFillRef.current.style.transition = "none";
        gaugeFillRef.current.style.width = "10%";
        gaugeFillRef.current.style.left = "50%";
        gaugeFillRef.current.style.transform = "translateX(-50%)";

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (gaugeFillRef.current) {
              gaugeFillRef.current.style.transition = "width 1s ease-out";
              gaugeFillRef.current.style.width = "100%";
            }
          });
        });
      }
    };

    // Initial animation
    animateGauge();

    // Set up interval for repeating animation
    const interval = setInterval(animateGauge, animationInterval);

    // Cleanup
    return () => clearInterval(interval);
  }, [animationInterval]);

  return (
    <div
      className="w-[1540px] h-[220px] font-virage bg-cover bg-center relative"
      style={{
        backgroundImage: `url('${sonometer?.publicScreenSettings?.gaugeImage || "/images/sonometer_bg.png"}')`,
      }}
    >
      {/* Sponsor */}
      <div
        className="sponsor"
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "260px",
          height: "220px",
          zIndex: 1,
        }}
      >
        <img
          src={
            sonometer?.publicScreenSettings.sponsorLogo ||
            assetBaseUrl + "/images/sponsor_left.png"
          }
          alt="Sponsor"
          style={{ width: "100%", height: "100%" }}
        />
      </div>

      {/* Main Container */}
      <div
        className="container"
        style={{
          position: "absolute",
          top: 0,
          right: 0,
          width: "calc(100% - 260px)",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          fontSize: "50px",
          fontWeight: "bold",
          // backgroundImage:
          //   "url('https://play-event-static.s3.eu-west-3.amazonaws.com/sonometer/images/gauge_bg.png')",
          // backgroundSize: "cover",
          // backgroundPosition: "left",
        }}
      >
        {/* Gauge Container */}
        <div
          className="gauge-container"
          style={{
            position: "absolute",
            left: "120px",
            width: "1130px",
            height: "52px",
            backgroundColor: "white",
            borderRadius: "26px",
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            ref={gaugeFillRef}
            id="gauge-fill"
            className="gauge-fill"
            style={{
              position: "absolute",
              left: "50%",
              top: 0,
              height: "100%",
              width: "100%",
              backgroundColor: "#E30613",
              borderRadius: "26px",
              transform: "translateX(-50%)",
              zIndex: 2,
            }}
          />
          <div
            id="decibel-value"
            style={{
              position: "relative",
              fontSize: "40px",
              fontWeight: "bold",
              zIndex: 3,
            }}
          >
            {displayValue.toFixed(1)} dB
          </div>
        </div>

        {/* Marker */}
        <div
          ref={gaugeMarkerRef}
          id="gaugeMarker"
          className="gauge-marker"
          style={{
            position: "absolute",
            display: !maxRecord ? "none" : "block",
            left: "630px", // Default position, will be updated by useEffect
            top: "calc(50% - 67px)", // Default position, will be updated by useEffect
            width: "19px",
            height: "104px",
            zIndex: 1,
            borderRadius: "2px",
          }}
        >
          <img
            src={assetBaseUrl + "/images/best_score.png"}
            alt="Marker"
            style={{ width: "100%", height: "100%" }}
          />
        </div>
      </div>
    </div>
  );
};

export default ScoreRevelation;
